import type { ReactNode } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Briefcase, ClipboardCheck, EuroIcon, GraduationCap, House, Info, UserSquare2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { activityStepSchema } from "./form-validation/activity-step.schema";
import { billingStepSchema } from "./form-validation/billing-step.schema";
import { studioStepSchema } from "./form-validation/studio-step.schema";
import { pedagogyStepSchema } from "./form-validation/pedagogy-step.schema";
import { publicProfileStepSchema } from "./form-validation/public-profile-step.schema";
import type { TeacherOnboardingFormInputs } from "./form-validation/full-form.schema";
import { ONBOARDING_STEP, ONBOARDING_STEP_LABELS } from "./form-validation/shared";
import type { PublicStudioListVM } from "@shared-kernel/application/ports/public/public-repository";
import { Alert } from "@shared-kernel/primary/shared/alert";
import { Stepper } from "@shared-kernel/primary/shared/stepper/stepper";
import { useStepperForm } from "@shared-kernel/primary/shared/stepper/use-stepper-form";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import type { FormSubmit } from "@shared-kernel/primary/shared/api-errors/action-form";
import type { RequestState } from "@redux/state/shared-types";
import { Form } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { FormSubmitErrorBanner } from "@shared-kernel/primary/shared/form-submit-error-banner/form-submit-error-banner";
import { Separator } from "@shared-kernel/primary/shared/shadcn/ui/separator";
import { ActivityStep } from "./subforms/teacher-onboarding.activity.subform";
import { BillingStep } from "./subforms/teacher-onboarding.billing.subform";
import { StudioStep } from "./subforms/teacher-onboarding.studio.subform";
import { RecapStep } from "./subforms/teacher-onboarding.recap.subform";
import { PedagogyStep } from "./subforms/teacher-onboarding.pedagogy.subform";
import type { ResumeField } from "@user-management-context/primary/teacher/teacher-pedagogy-subform/teacher-pedagogy.subform";
import { PublicProfileStep } from "./subforms/teacher-onboarding.public-profile.subform";

interface PedagogyUploadProps {
  onUploadResume: (field: ResumeField, file: File) => void;
  onUploadMethodology: (rowIndex: number, file: File) => void;
  uploadProcessing: RequestState;
}

interface BodyProps extends PedagogyUploadProps {
  publicStudios: PublicStudioListVM[];
  onSubmit: FormSubmit;
  submitting: boolean;
}

interface Props extends BodyProps {
  form: UseFormReturn<TeacherOnboardingFormInputs>;
}

const STEP_SCHEMAS = {
  [ONBOARDING_STEP.ACTIVITY]: activityStepSchema,
  [ONBOARDING_STEP.PEDAGOGY]: pedagogyStepSchema,
  [ONBOARDING_STEP.PUBLIC_PROFILE]: publicProfileStepSchema,
  [ONBOARDING_STEP.BILLING]: billingStepSchema,
  [ONBOARDING_STEP.STUDIO]: studioStepSchema,
} as const;

interface StepComponentProps extends PedagogyUploadProps {
  publicStudios: PublicStudioListVM[];
  onEditStep: (stepId: number) => void;
}

type StepDefinition = {
  id: number;
  label: string;
  title: string;
  intro?: ReactNode;
  Icon: LucideIcon;
  Component: React.ComponentType<StepComponentProps>;
};

const BASE_STEPS: StepDefinition[] = [
  {
    id: ONBOARDING_STEP.ACTIVITY,
    label: ONBOARDING_STEP_LABELS[ONBOARDING_STEP.ACTIVITY],
    title: "Votre activité",
    intro:
      "Ces informations servent à établir votre contrat de sous-traitance avec Les Ateliers OO et à la facturation. Merci de les renseigner avec précision.",
    Icon: Briefcase,
    Component: ActivityStep,
  },
  {
    id: ONBOARDING_STEP.PEDAGOGY,
    label: ONBOARDING_STEP_LABELS[ONBOARDING_STEP.PEDAGOGY],
    title: "Ma pédagogie",
    intro:
      "Vos CV, certifications et méthodologies pédagogiques. Ces informations alimentent votre contrat de sous-traitance et les dossiers de financement transmis à l'AFDAS.",
    Icon: GraduationCap,
    Component: PedagogyStep,
  },
  {
    id: ONBOARDING_STEP.PUBLIC_PROFILE,
    label: ONBOARDING_STEP_LABELS[ONBOARDING_STEP.PUBLIC_PROFILE],
    title: "Mon profil public",
    intro: "Ces informations apparaîtront sur votre profil public lorsqu'il sera complet & visible.",
    Icon: UserSquare2,
    Component: PublicProfileStep,
  },
  {
    id: ONBOARDING_STEP.BILLING,
    label: ONBOARDING_STEP_LABELS[ONBOARDING_STEP.BILLING],
    title: "Tarif et facturation",
    intro: "Votre tarif horaire standard et votre IBAN pour le règlement de vos prestations par Les Ateliers OO.",
    Icon: EuroIcon,
    Component: BillingStep,
  },
  {
    id: ONBOARDING_STEP.STUDIO,
    label: ONBOARDING_STEP_LABELS[ONBOARDING_STEP.STUDIO],
    title: "Studio principal",
    Icon: House,
    Component: StudioStep,
  },
  {
    id: ONBOARDING_STEP.RECAP,
    label: ONBOARDING_STEP_LABELS[ONBOARDING_STEP.RECAP],
    title: "Récapitulatif",
    intro: "Vérifiez les informations ci-dessous avant de finaliser votre onboarding. Vous pouvez modifier chaque section si nécessaire.",
    Icon: ClipboardCheck,
    Component: RecapStep,
  },
];

const STEP_IDS = BASE_STEPS.map(s => s.id);

export const TeacherOnboarding = ({ form, ...props }: Props) => {
  return (
    <Form {...form}>
      <TeacherOnboardingBody {...props} />
    </Form>
  );
};

const TeacherOnboardingBody = ({
  publicStudios,
  onSubmit,
  submitting,
  onUploadResume,
  onUploadMethodology,
  uploadProcessing,
}: BodyProps) => {
  const { currentStepIndex, goNext, goBack, goToStep, isLastStep } = useStepperForm<TeacherOnboardingFormInputs>({
    stepIds: STEP_IDS,
    stepSchemas: STEP_SCHEMAS,
  });

  const currentStep = BASE_STEPS[currentStepIndex];

  if (!currentStep) {
    return <div>Étape non trouvée</div>;
  }

  const { Component: CurrentStepComponent } = currentStep;

  return (
    <div className="space-y-6">
      <Stepper steps={BASE_STEPS} currentStep={currentStepIndex} onStepClick={goToStep} />
      <Separator />
      <form
        onSubmit={
          isLastStep
            ? onSubmit
            : e => {
                e.preventDefault();
                void goNext();
              }
        }
        className="space-y-4"
      >
        {currentStep.intro && (
          <Alert variant="info" className="flex items-start gap-3 text-sm leading-relaxed">
            <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
            <p>{currentStep.intro}</p>
          </Alert>
        )}
        <CurrentStepComponent
          publicStudios={publicStudios}
          onEditStep={goToStep}
          onUploadResume={onUploadResume}
          onUploadMethodology={onUploadMethodology}
          uploadProcessing={uploadProcessing}
        />

        <FormSubmitErrorBanner />

        <div className="flex justify-between pt-4">
          {currentStepIndex > 0 ? (
            <Button onClick={goBack} type="button" variant="outline">
              Retour
            </Button>
          ) : (
            <span />
          )}
          <Button type="submit" disabled={isLastStep && submitting}>
            {isLastStep ? "Confirmer et finaliser" : "Suivant"}
          </Button>
        </div>
      </form>
    </div>
  );
};
